import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Container, Row, Col, Card, Button, Form, Modal, Badge, Spinner, InputGroup } from 'react-bootstrap';
import {
  FaUserMd, FaPlus, FaEdit, FaTrash, FaSearch, FaVideo, FaClinicMedical,
  FaCalendarAlt, FaClock, FaPhoneAlt, FaEnvelope, FaGraduationCap, FaCheck, FaTimes
} from 'react-icons/fa';
import { get, post, put, del } from '../utils/api';
import { useToast } from './ToastContext';
import { CURRENCY_SYMBOL } from '../config';
import './DoctorManagement.css';

const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const DEFAULT_TIME_SLOTS = [
  '09:00 AM', '09:30 AM', '10:00 AM', '10:30 AM', '11:00 AM', '11:30 AM',
  '02:00 PM', '02:30 PM', '03:00 PM', '03:30 PM', '04:00 PM', '04:30 PM', '05:00 PM'
];

const SPECIALTY_OPTIONS = [
  'General Physician', 'Cardiologist', 'Dentist', 'Dermatologist',
  'Neurologist', 'Orthopedic', 'Pediatrician', 'Psychiatrist',
  'Gynecologist', 'Ophthalmologist', 'ENT Specialist', 'Urologist'
];

const DoctorManagement = () => {
  const { showToast } = useToast();
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSpecialty, setSelectedSpecialty] = useState('all');

  // Modal State
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [currentDoctorId, setCurrentDoctorId] = useState(null);
  const [isCustomSpecialty, setIsCustomSpecialty] = useState(false);

  // Dynamic specialties: combine presets with all distinct specialties from database
  const dynamicSpecialties = useMemo(() => {
    const fromDocs = doctors.map(d => d.specialty).filter(Boolean);
    const set = new Set([...SPECIALTY_OPTIONS, ...fromDocs]);
    return Array.from(set).sort();
  }, [doctors]);

  const [formData, setFormData] = useState({
    name: '',
    specialty: 'General Physician',
    qualification: '',
    experience_years: 5,
    accepts_online: true,
    accepts_clinic: true,
    phone: '',
    email: '',
    consultation_fee_online: 0,
    consultation_fee_clinic: 0,
    available_days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
    time_slots: DEFAULT_TIME_SLOTS,
    default_google_meet_link: '',
    is_active: 1,
  });

  const [customSlotInput, setCustomSlotInput] = useState('');

  const fetchDoctors = useCallback(async () => {
    try {
      setLoading(true);
      const res = await get('/doctors?active_only=false');
      if (res && res.success) {
        setDoctors(res.data || []);
      }
    } catch (err) {
      showToast(err.message || 'Failed to fetch doctors', 'danger');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    fetchDoctors();
  }, [fetchDoctors]);

  const handleOpenAdd = () => {
    setIsEditing(false);
    setIsCustomSpecialty(false);
    setCurrentDoctorId(null);
    setFormData({
      name: '',
      specialty: 'General Physician',
      qualification: '',
      experience_years: 5,
      accepts_online: true,
      accepts_clinic: true,
      phone: '',
      email: '',
      consultation_fee_online: 0,
      consultation_fee_clinic: 0,
      available_days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
      time_slots: [...DEFAULT_TIME_SLOTS],
      default_google_meet_link: '',
      is_active: 1,
    });
    setShowModal(true);
  };

  const handleOpenEdit = (doc) => {
    setIsEditing(true);
    setCurrentDoctorId(doc.doctor_id);
    const isCustom = doc.specialty && !SPECIALTY_OPTIONS.includes(doc.specialty);
    setIsCustomSpecialty(isCustom);
    setFormData({
      name: doc.name || '',
      specialty: doc.specialty || 'General Physician',
      qualification: doc.qualification || '',
      experience_years: doc.experience_years || 0,
      accepts_online: doc.accepts_online !== undefined ? Boolean(doc.accepts_online) : true,
      accepts_clinic: doc.accepts_clinic !== undefined ? Boolean(doc.accepts_clinic) : true,
      phone: doc.phone || '',
      email: doc.email || '',
      consultation_fee_online: doc.consultation_fee_online || 0,
      consultation_fee_clinic: doc.consultation_fee_clinic || 0,
      available_days: Array.isArray(doc.available_days) ? doc.available_days : [],
      time_slots: Array.isArray(doc.time_slots) && doc.time_slots.length > 0 ? doc.time_slots : [...DEFAULT_TIME_SLOTS],
      default_google_meet_link: doc.default_google_meet_link || '',
      is_active: doc.is_active ? 1 : 0,
    });
    setShowModal(true);
  };

  const toggleDay = (day) => {
    setFormData(prev => {
      const exists = prev.available_days.includes(day);
      return {
        ...prev,
        available_days: exists
          ? prev.available_days.filter(d => d !== day)
          : [...prev.available_days, day]
      };
    });
  };

  const addCustomSlot = () => {
    if (!customSlotInput.trim()) return;
    const slot = customSlotInput.trim();
    if (!formData.time_slots.includes(slot)) {
      setFormData(prev => ({ ...prev, time_slots: [...prev.time_slots, slot] }));
    }
    setCustomSlotInput('');
  };

  const removeSlot = (slot) => {
    setFormData(prev => ({
      ...prev,
      time_slots: prev.time_slots.filter(s => s !== slot)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      showToast('Please enter doctor name', 'warning');
      return;
    }
    if (!formData.accepts_online && !formData.accepts_clinic) {
      showToast('Doctor must accept at least one consultation mode (Online Video or Clinic Visit)', 'warning');
      return;
    }

    try {
      setSubmitting(true);
      if (isEditing) {
        const res = await put(`/doctors/${currentDoctorId}`, formData);
        if (res && res.success) {
          showToast('Doctor details updated successfully!', 'success');
          setShowModal(false);
          fetchDoctors();
        }
      } else {
        const res = await post('/doctors', formData);
        if (res && res.success) {
          showToast('Doctor added successfully!', 'success');
          setShowModal(false);
          fetchDoctors();
        }
      }
    } catch (err) {
      showToast(err.message || 'Failed to save doctor', 'danger');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(`Are you sure you want to deactivate or remove ${doc.name}?`)) return;
    try {
      const res = await del(`/doctors/${doc.doctor_id}`);
      if (res && res.success) {
        showToast(res.message || 'Doctor updated successfully', 'success');
        fetchDoctors();
      }
    } catch (err) {
      showToast(err.message || 'Failed to delete doctor', 'danger');
    }
  };

  const filteredDoctors = doctors.filter(doc => {
    const matchesSearch =
      (doc.name && doc.name.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (doc.specialty && doc.specialty.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (doc.qualification && doc.qualification.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesSpecialty = selectedSpecialty === 'all' || doc.specialty === selectedSpecialty;
    return matchesSearch && matchesSpecialty;
  });

  return (
    <Container fluid className="doctor-management-container py-4">
      {/* Header Banner */}
      <div className="doctor-header-card mb-4">
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3">
          <div>
            <h2 className="doctor-title d-flex align-items-center gap-2">
              <FaUserMd className="text-primary" /> Doctor & Availability Management
            </h2>
            <p className="doctor-subtitle mb-0">
              Configure clinic and online consulting doctors, consultation fees, available schedules, and Google Meet rooms.
            </p>
          </div>
          <Button variant="primary" className="add-doctor-btn d-flex align-items-center gap-2" onClick={handleOpenAdd}>
            <FaPlus /> Add New Doctor
          </Button>
        </div>
      </div>

      {/* Controls Bar */}
      <div className="doctor-controls-card mb-4">
        <Row className="g-3">
          <Col md={6} lg={4}>
            <InputGroup>
              <InputGroup.Text className="bg-transparent border-end-0">
                <FaSearch className="text-muted" />
              </InputGroup.Text>
              <Form.Control
                type="text"
                placeholder="Search by doctor name, specialty, or qualification..."
                className="border-start-0"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </InputGroup>
          </Col>
          <Col md={6} lg={3}>
            <Form.Select
              value={selectedSpecialty}
              onChange={(e) => setSelectedSpecialty(e.target.value)}
            >
              <option value="all">All Specialties ({doctors.length})</option>
              {dynamicSpecialties.map((spec, i) => (
                <option key={i} value={spec}>{spec}</option>
              ))}
            </Form.Select>
          </Col>
          <Col className="text-md-end text-muted d-flex align-items-center justify-content-md-end">
            <span>Showing <strong>{filteredDoctors.length}</strong> of <strong>{doctors.length}</strong> doctors</span>
          </Col>
        </Row>
      </div>

      {/* Doctor Cards Grid */}
      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2 text-muted">Loading doctor schedules...</p>
        </div>
      ) : filteredDoctors.length === 0 ? (
        <div className="text-center py-5 no-doctors-card">
          <FaUserMd size={48} className="text-muted mb-3 opacity-50" />
          <h5>No doctors found</h5>
          <p className="text-muted">Try changing your search query or add a new doctor.</p>
          <Button variant="outline-primary" size="sm" onClick={handleOpenAdd}>
            <FaPlus className="me-1" /> Add Doctor
          </Button>
        </div>
      ) : (
        <Row className="g-4">
          {filteredDoctors.map((doc) => (
            <Col key={doc.doctor_id} lg={4} md={6}>
              <Card className={`doctor-card h-100 ${!doc.is_active ? 'doctor-card-inactive' : ''}`}>
                <Card.Body className="d-flex flex-column">
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <div>
                      <h5 className="doctor-card-name mb-1">{doc.name}</h5>
                      <Badge bg="primary" className="doctor-specialty-badge mb-1">
                        {doc.specialty}
                      </Badge>
                      {doc.qualification && (
                        <div className="doctor-qualification text-muted small d-flex align-items-center gap-1">
                          <FaGraduationCap /> {doc.qualification}
                        </div>
                      )}
                    </div>
                    <Badge bg={doc.is_active ? 'success' : 'secondary'}>
                      {doc.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>

                  {/* Consultation Modes Badges */}
                  <div className="d-flex flex-wrap gap-1 my-2">
                    {doc.accepts_online && doc.accepts_clinic ? (
                      <>
                        <span className="badge bg-info-subtle text-info border border-info-subtle small d-inline-flex align-items-center gap-1">
                          <FaVideo size={10} /> Online Video
                        </span>
                        <span className="badge bg-success-subtle text-success border border-success-subtle small d-inline-flex align-items-center gap-1">
                          <FaClinicMedical size={10} /> Clinic Visit
                        </span>
                      </>
                    ) : doc.accepts_online ? (
                      <span className="badge bg-info-subtle text-info border border-info-subtle small d-inline-flex align-items-center gap-1">
                        <FaVideo size={10} /> Online Consultation Only
                      </span>
                    ) : (
                      <span className="badge bg-warning-subtle text-warning border border-warning-subtle small d-inline-flex align-items-center gap-1">
                        <FaClinicMedical size={10} /> 🏥 Clinic Visit Only
                      </span>
                    )}
                  </div>

                  {/* Consultation Fees */}
                  <div className="fee-badge-container my-2 p-2 rounded">
                    <div className="d-flex justify-content-between align-items-center small mb-1">
                      <span className="d-flex align-items-center gap-1 text-info">
                        <FaVideo /> Online Video (G-Meet):
                      </span>
                      {doc.accepts_online ? (
                        <strong>{CURRENCY_SYMBOL}{doc.consultation_fee_online}</strong>
                      ) : (
                        <span className="text-muted small fst-italic">Not Offered</span>
                      )}
                    </div>
                    <div className="d-flex justify-content-between align-items-center small">
                      <span className="d-flex align-items-center gap-1 text-success">
                        <FaClinicMedical /> Physical Clinic Visit:
                      </span>
                      {doc.accepts_clinic ? (
                        <strong>{CURRENCY_SYMBOL}{doc.consultation_fee_clinic}</strong>
                      ) : (
                        <span className="text-muted small fst-italic">Not Offered</span>
                      )}
                    </div>
                  </div>

                  {/* Contact Info */}
                  <div className="doctor-contact-info small text-muted my-2">
                    {doc.phone && (
                      <div className="d-flex align-items-center gap-2 mb-1">
                        <FaPhoneAlt size={12} /> {doc.phone}
                      </div>
                    )}
                    {doc.email && (
                      <div className="d-flex align-items-center gap-2 mb-1">
                        <FaEnvelope size={12} /> {doc.email}
                      </div>
                    )}
                    {doc.default_google_meet_link && (
                      <div className="d-flex align-items-center gap-2 text-primary text-truncate">
                        <FaVideo size={12} />
                        <a href={doc.default_google_meet_link} target="_blank" rel="noreferrer" className="text-truncate">
                          {doc.default_google_meet_link}
                        </a>
                      </div>
                    )}
                  </div>

                  {/* Available Days */}
                  <div className="mt-2 mb-3">
                    <span className="small text-muted d-block mb-1">
                      <FaCalendarAlt className="me-1" /> Available Days:
                    </span>
                    <div className="d-flex flex-wrap gap-1">
                      {doc.available_days && doc.available_days.length > 0 ? (
                        doc.available_days.map((day, idx) => (
                          <span key={idx} className="day-pill">
                            {day.slice(0, 3)}
                          </span>
                        ))
                      ) : (
                        <span className="small text-muted fst-italic">No days set</span>
                      )}
                    </div>
                  </div>

                  {/* Card Actions */}
                  <div className="mt-auto pt-3 border-top d-flex gap-2 justify-content-end">
                    <Button variant="outline-primary" size="sm" onClick={() => handleOpenEdit(doc)}>
                      <FaEdit className="me-1" /> Edit Schedule
                    </Button>
                    <Button variant="outline-danger" size="sm" onClick={() => handleDelete(doc)}>
                      <FaTrash />
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      )}

      {/* Add / Edit Doctor Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered scrollable className="doctor-modal">
        <Modal.Header closeButton>
          <Modal.Title className="d-flex align-items-center gap-2">
            <FaUserMd className="text-primary" />
            {isEditing ? 'Edit Doctor & Schedule' : 'Add New Doctor'}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit} className="d-flex flex-column flex-grow-1 overflow-hidden" style={{ minHeight: 0, height: "100%" }}>
          <Modal.Body className="p-4 flex-grow-1 overflow-auto" style={{ minHeight: 0 }}>
            <Row className="g-3 mb-3">
              <Col md={7}>
                <Form.Group>
                  <Form.Label>Doctor Full Name *</Form.Label>
                  <Form.Control
                    type="text"
                    required
                    placeholder="e.g. Dr. Sarah Johnson"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  />
                </Form.Group>
              </Col>
              <Col md={5}>
                <Form.Group>
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <Form.Label className="mb-0">Specialty *</Form.Label>
                    <button
                      type="button"
                      className="btn btn-link btn-sm p-0 text-decoration-none fw-semibold"
                      style={{ fontSize: '0.8rem' }}
                      onClick={() => {
                        const nextState = !isCustomSpecialty;
                        setIsCustomSpecialty(nextState);
                        if (nextState) {
                          setFormData(prev => ({ ...prev, specialty: '' }));
                        } else {
                          setFormData(prev => ({ ...prev, specialty: dynamicSpecialties[0] || 'General Physician' }));
                        }
                      }}
                    >
                      {isCustomSpecialty ? '← Select from list' : '+ Type New Specialty'}
                    </button>
                  </div>

                  {!isCustomSpecialty ? (
                    <Form.Select
                      required
                      value={formData.specialty}
                      onChange={(e) => {
                        if (e.target.value === '__NEW__') {
                          setIsCustomSpecialty(true);
                          setFormData({ ...formData, specialty: '' });
                        } else {
                          setFormData({ ...formData, specialty: e.target.value });
                        }
                      }}
                    >
                      <option value="">-- Select Specialty --</option>
                      {dynamicSpecialties.map((spec, i) => (
                        <option key={i} value={spec}>{spec}</option>
                      ))}
                      <option value="__NEW__" className="text-primary fw-bold">
                        ➕ Add New / Custom Specialty...
                      </option>
                    </Form.Select>
                  ) : (
                    <InputGroup>
                      <Form.Control
                        type="text"
                        required
                        placeholder="e.g. ENT Specialist, Oncologist..."
                        value={formData.specialty}
                        onChange={(e) => setFormData({ ...formData, specialty: e.target.value })}
                        autoFocus
                      />
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => {
                          setIsCustomSpecialty(false);
                          setFormData(prev => ({ ...prev, specialty: dynamicSpecialties[0] || 'General Physician' }));
                        }}
                        title="Back to dropdown list"
                      >
                        ✕
                      </Button>
                    </InputGroup>
                  )}
                  <Form.Text className="text-muted small d-block">
                    {isCustomSpecialty
                      ? '💡 Type a new specialty. It will be created and available across the entire app.'
                      : 'Choose an existing specialty or click "+ Type New Specialty".'}
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <Row className="g-3 mb-3">
              <Col md={7}>
                <Form.Group>
                  <Form.Label>Qualification / Degrees</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="e.g. MBBS, MD (Internal Medicine)"
                    value={formData.qualification}
                    onChange={(e) => setFormData({ ...formData, qualification: e.target.value })}
                  />
                </Form.Group>
              </Col>
              <Col md={5}>
                <Form.Group>
                  <Form.Label>Experience (Years)</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    value={formData.experience_years}
                    onChange={(e) => setFormData({ ...formData, experience_years: e.target.value })}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row className="g-3 mb-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Phone Number</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="+91 9876543210"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Email Address</Form.Label>
                  <Form.Control
                    type="email"
                    placeholder="doctor@medicin.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  />
                </Form.Group>
              </Col>
            </Row>

            <hr className="my-3" />

            {/* Consultation Modes Offered */}
            <h6 className="section-subtitle mb-2">🩺 Consultation Modes Offered</h6>
            <div className="consultation-modes-box p-3 rounded mb-3 border">
              <Row className="g-3">
                <Col md={6}>
                  <Form.Check
                    type="switch"
                    id="accepts-online-switch"
                    label={
                      <span className="fw-semibold d-flex align-items-center gap-1 text-info">
                        <FaVideo /> Online Video Consultation
                      </span>
                    }
                    checked={formData.accepts_online}
                    onChange={(e) => setFormData({ ...formData, accepts_online: e.target.checked })}
                  />
                  <Form.Text className="text-muted small d-block ms-4">
                    Patient connects remotely via Google Meet & pays online via Razorpay.
                  </Form.Text>
                </Col>
                <Col md={6}>
                  <Form.Check
                    type="switch"
                    id="accepts-clinic-switch"
                    label={
                      <span className="fw-semibold d-flex align-items-center gap-1 text-success">
                        <FaClinicMedical /> Physical Clinic Visit
                      </span>
                    }
                    checked={formData.accepts_clinic}
                    onChange={(e) => setFormData({ ...formData, accepts_clinic: e.target.checked })}
                  />
                  <Form.Text className="text-muted small d-block ms-4">
                    Patient visits the physical clinic & pays at counter upon arrival.
                  </Form.Text>
                </Col>
              </Row>
            </div>

            {/* Consultation Fees */}
            <h6 className="section-subtitle mb-2">💰 Consultation Fees ({CURRENCY_SYMBOL})</h6>
            <Row className="g-3 mb-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="d-flex align-items-center gap-1 text-info">
                    <FaVideo /> Online Video Consultation Fee ({CURRENCY_SYMBOL})
                  </Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    step="10"
                    disabled={!formData.accepts_online}
                    value={formData.consultation_fee_online}
                    onChange={(e) => setFormData({ ...formData, consultation_fee_online: e.target.value })}
                  />
                  <Form.Text className="text-muted">
                    {formData.accepts_online
                      ? 'Mandatory online payment (Razorpay) in mobile app.'
                      : '⚠️ Disabled: This doctor does not accept online video consultations.'}
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="d-flex align-items-center gap-1 text-success">
                    <FaClinicMedical /> Physical Clinic Visit Fee ({CURRENCY_SYMBOL})
                  </Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    step="10"
                    disabled={!formData.accepts_clinic}
                    value={formData.consultation_fee_clinic}
                    onChange={(e) => setFormData({ ...formData, consultation_fee_clinic: e.target.value })}
                  />
                  <Form.Text className="text-muted">
                    {formData.accepts_clinic
                      ? 'Paid in person at the clinic counter.'
                      : '⚠️ Disabled: This doctor does not accept physical clinic visits.'}
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            {/* Google Meet Room */}
            <Form.Group className="mb-3">
              <Form.Label className="d-flex align-items-center gap-1 text-primary">
                <FaVideo /> Default Google Meet Link
              </Form.Label>
              <Form.Control
                type="url"
                disabled={!formData.accepts_online}
                placeholder="https://meet.google.com/abc-defg-hij"
                value={formData.default_google_meet_link}
                onChange={(e) => setFormData({ ...formData, default_google_meet_link: e.target.value })}
              />
              <Form.Text className="text-muted">
                {formData.accepts_online
                  ? 'This Google Meet link will be provided to patients for online consultations.'
                  : 'Disabled because online video consultation is turned off for this doctor.'}
              </Form.Text>
            </Form.Group>

            <hr className="my-3" />

            {/* Available Days */}
            <h6 className="section-subtitle mb-2">📅 Available Days</h6>
            <div className="d-flex flex-wrap gap-2 mb-3">
              {DAYS_OF_WEEK.map((day) => {
                const isSelected = formData.available_days.includes(day);
                return (
                  <button
                    key={day}
                    type="button"
                    className={`btn btn-sm day-toggle-btn ${isSelected ? 'btn-primary' : 'btn-outline-secondary'}`}
                    onClick={() => toggleDay(day)}
                  >
                    {isSelected && <FaCheck className="me-1" />} {day}
                  </button>
                );
              })}
            </div>

            {/* Time Slots */}
            <h6 className="section-subtitle mb-2">⏰ Consultation Time Slots</h6>
            <div className="d-flex flex-wrap gap-1 mb-2">
              {formData.time_slots.map((slot, idx) => (
                <Badge key={idx} className="badge slot-badge border p-2 d-flex align-items-center gap-1">
                  <FaClock size={10} className="text-muted" /> {slot}
                  <FaTimes className="text-danger ms-1 cursor-pointer" onClick={() => removeSlot(slot)} />
                </Badge>
              ))}
            </div>
            <InputGroup size="sm" className="mb-3" style={{ maxWidth: '300px' }}>
              <Form.Control
                type="text"
                placeholder="e.g. 06:00 PM"
                value={customSlotInput}
                onChange={(e) => setCustomSlotInput(e.target.value)}
              />
              <Button variant="outline-primary" onClick={addCustomSlot}>Add Slot</Button>
            </InputGroup>

            {/* Status */}
            <Form.Check
              type="switch"
              id="active-switch"
              label="Doctor is currently active & accepting appointments"
              checked={formData.is_active === 1}
              onChange={(e) => setFormData({ ...formData, is_active: e.target.checked ? 1 : 0 })}
            />
          </Modal.Body>
          <Modal.Footer className="flex-shrink-0">
            <Button variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button>
            <Button variant="primary" type="submit" disabled={submitting}>
              {submitting ? 'Saving...' : (isEditing ? 'Save Changes' : 'Create Doctor')}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
  );
};

export default DoctorManagement;
